import React, { useState } from 'react';
import Loading from '../../../../Components/Loading';
import styles from './PatientMedicalHistory.module.scss';
import ScrollContainer from '../../../Base/Components/ScrollContainer';
import usePatientMedicalHistory from '../../Hooks/usePatientMedicalHistory';
import PatientMedicalHistoryItem from './PatientMedicalHistoryItem';
import { Theme } from '../../../../Enums/Theme.enum';
import LinkButton from '../../../../Components/Button/LinkButton';
import MagicForm from '../../../../Components/Form/MagicForm';
import PatientMedicalHistoryVersionsModal from './PatientMedicalHistoryVersionsModal';
import PatientMedicalHistoryPrintModal from './PatientMedicalHistoryPrintModal';
import useRestApiClient from '../../../Base/Hooks/useRestApiClient';

interface Props {
	patientId: string;
}

const PatientMedicalHistory: React.FC<Props> = ({ patientId }) => {
	const [isEditMode, setIsEditMode] = useState(false);
	const [isPrintOpen, setIsPrintOpen] = useState(false);
	const [isHistoryOpen, setIsHistoryOpen] = useState(false);
	const {
		medicalHistory,
		medicalHistoryFormScheme,
		updateMedicalHistory,
		reFetch,
		isLoading,
		medicalHistoryDocumentId,
	} = usePatientMedicalHistory(patientId);
	const { processResult } = useRestApiClient();

	const handleSubmit = async (values: any) => {
		const response = await updateMedicalHistory(values);
		if (response?.status === 200) {
			processResult(response, async () => {
				await reFetch();
				setIsEditMode(false);
			});
		}
	};

	if (isLoading) {
		return <Loading />;
	}

	let component: React.ReactElement;

	if (isEditMode) {
		component = (
			<div className={styles.formWrapper}>
				<MagicForm
					formScheme={medicalHistoryFormScheme}
					onSubmit={handleSubmit}
				/>
			</div>
		);
	} else {
		component = (
			<>
				<div className={styles.controls}>
					<LinkButton
						title='component.patientMedicalHistory.print'
						theme={Theme.BLUE}
						firstUpper
						onClick={() => setIsPrintOpen(true)}
					/>
					<LinkButton
						title='component.patientMedicalHistory.history'
						theme={Theme.BLUE}
						firstUpper
						onClick={() => setIsHistoryOpen(true)}
					/>
					<LinkButton
						title='component.patientMedicalHistory.edit'
						theme={Theme.BLUE}
						firstUpper
						onClick={() => setIsEditMode(true)}
					/>
				</div>
				{medicalHistory.map(item => (
					<PatientMedicalHistoryItem item={item} key={item.key} />
				))}
				{isPrintOpen && (
					<PatientMedicalHistoryPrintModal
						isOpen
						onClose={() => setIsPrintOpen(false)}
						patientId={patientId}
					/>
				)}
				{isHistoryOpen && medicalHistoryDocumentId && (
					<PatientMedicalHistoryVersionsModal
						isOpen
						patientId={patientId}
						onClose={() => setIsHistoryOpen(false)}
						medicalHistoryDocumentId={medicalHistoryDocumentId}
					/>
				)}
			</>
		);
	}

	return (
		<ScrollContainer>
			<div className={styles.wrapper}>{component}</div>
		</ScrollContainer>
	);
};

export default PatientMedicalHistory;
